<template>
    <v-card>
        <v-card-text>
            <template v-if="isLinking">
                <v-flex xs12 mb-5 centeredIcon>
                    <loading :message="$t('LTI_LINK_STEP_LOADING')" />
                </v-flex>
            </template>
            <template v-else-if="successfullyLinked">
                <v-flex xs12 mb-5 centeredIcon>
                    <v-icon size="48" color="success">done</v-icon>
                </v-flex>
                <v-flex xs12 mb-5>
                    <span v-html="$t('LTI_LINK_STEP_SUCCEEDED_TEXT', {ltiContextName: ltiIdentity.contextName, courseName: course.name})" />
                </v-flex>
            </template>
            <template v-else>
                <v-flex xs12 mb-5 centeredIcon>
                    <v-icon size="48" color="error">error</v-icon>
                </v-flex>
                <v-flex xs12 mb-5>
                    <span v-html="$t('LTI_LINK_STEP_FAILED_TEXT')" />
                </v-flex>
            </template>
        </v-card-text>
        <v-card-actions class="pl-2 pr-3 pb-3">
            <v-spacer></v-spacer>
            <template v-if="isLinking || launchFailed">
                <!-- Show nothing -->
            </template>
            <v-btn v-else-if="successfullyLinked" color="primary" class="elevation-0" @click="confirm" :disabled="isLoading">
                {{ $t("LTI_LINK_STEP_CONFIRM") }}
            </v-btn>
            <v-btn v-else color="confirm" text @click="cancel" :disabled="isLoading">
                {{ $t("LTI_LINK_STEP_CANCEL") }}
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { CourseApplication, CourseApplicationType, ICourse, UserRole } from "@cyber-range/cyber-range-api-user-client";
import Config from "@/config";
import Loading from "@/components/utils/Loading.vue";
import { ConflictApiClientError } from "@cyber-range/cyber-range-api-client";
import { useErrorStore } from "@/stores/errorStore";
import { useApiClientStore } from "@/stores/apiClientStore";
import { useAuthenticationStore } from "@/stores/authenticationStore";

@Component({components: {Loading}})
export default class LinkStep extends Vue 
{
    @Prop(Object) course:ICourse;
    @Prop(Boolean) active:boolean;
    get ltiIdentity() { return useAuthenticationStore().ltiIdentity; }
    get ltiToken() { return useAuthenticationStore().ltiToken; }
    get isLoading() { return useApiClientStore().isLoading; }
    get userApiClient() { return useApiClientStore().userApiClient; }
    get backgroundAuthenticationApiClient() { return useApiClientStore().backgroundAuthenticationApiClient; }
    getSsoToken = () => useAuthenticationStore().getSsoToken();
    loadingBegin = () => useApiClientStore().loadingBegin();
    login = (payload: { token: string, provider: string }) => useAuthenticationStore().login(payload);
    defaultErrorHandler = (e: Error) => useErrorStore().setError(e);

    isLinking:boolean = true;
    launchFailed:boolean = false;
    successfullyLinked:boolean = false;

    @Watch("active")
    async onActiveChanged()
    {
        if(!this.active) return;
        
        this.isLinking = true;

        try
        {
            let courseApplication = new CourseApplication({
                type: CourseApplicationType.Lti13,
                courseId: this.course.id,
                deploymentId: this.ltiIdentity.deploymentId,
                organizationId: this.ltiIdentity.organizationId,
                organizationApplicationId: this.ltiIdentity.organizationApplicationId,
                referenceId: this.ltiIdentity.contextId,
                referenceUrl: '',
            });

            await this.userApiClient.createCourseApplication(this.course.id, courseApplication);

            try
            {
                await this.backgroundAuthenticationApiClient.addCurrentIdentityAlternateIds([this.ltiIdentity.sub]);
            }
            catch(e)
            {
                if(e instanceof ConflictApiClientError)
                {
                    // The user already exists in the course. Do nothing.  
                    // We will promote/demote the user by calling User API's createUserByLtiToken() below.
                }
                else
                {
                    throw e;
                }
            }

            // Re-add the user to the course to ensure the role is correct. 
            // At this point, the LTI token should have contextId that User API can use to determine a courseId.
            if(this.ltiIdentity.roles.some(role => [UserRole.Instructor, UserRole.Student, UserRole.TA].includes(<any>role)))
            {
                try
                {
                    await this.userApiClient.createUserByLtiToken(this.ltiToken, {errorHandler: () => {}});
                }
                catch (e)
                {
                    if (e.statusCode === 402 && e.message.includes('payment'))
                    {
                        // Do nothing. Everything else succeeded. When redirection happens, the user will be sent to the payment page.
                    }
                    else
                    {
                        this.defaultErrorHandler(e);
                        throw e;
                    }
                }
            }

            this.successfullyLinked = true;
        }
        finally
        {
            this.isLinking = false;
        }

    }
    async cancel()
    {
        this.$emit('cancel');
    }
    async confirm()
    {
        if(await this.login({provider:'vcr', token: this.ltiToken}))
        {
            let ssoToken = await this.getSsoToken();

            // Disable all buttons while the user is being redirected to the destination.
            this.loadingBegin();

            window.location.href = new URL(`/courses/${this.course.id}?s=${ssoToken}`, Config.CYBER_RANGE_UI_BASE_URL).href;
        }
        else
        {
            useErrorStore().setError(new Error(this.$t('LTI_LINK_STEP_LAUNCH_FAILED').toString()));
            this.launchFailed = true;
        }
    }
}
</script>

<style scoped>
.centeredIcon
{
    text-align: center;
}
</style>